import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';

// Calendar changes proposed by the server's sessions (ADR-024)

export function useCalendarProposals(serverId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['calendar-proposals', serverId],
    queryFn: () => api.calendar.proposals(serverId),
    select: (d) => d.proposals,
    // A session waits on the decision: keep the list fresh while the page is open
    refetchInterval: 15_000,
    // and when the operator comes back to the tab: the list must never say nothing waits when something does
    refetchOnWindowFocus: 'always',
  });
}

/** The sessions of the server that wait on a decision of the operator (#918): a calendar change they proposed, pending */
export function useSessionsAwaitingDecision(serverId: string): Set<string> {
  const { data } = useCalendarProposals(serverId);
  return new Set((data ?? []).filter((p) => p.status === 'pending' && p.requestedBySession).map((p) => p.requestedBySession!));
}

export function useDecideProposal(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ proposalId, decision }: { proposalId: string; decision: 'approve' | 'reject' }) => api.calendar.decide(serverId, proposalId, decision),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['calendar-proposals', serverId] });
      void qc.invalidateQueries({ queryKey: ['inbox', serverId] });
    },
  });
}
